import { useEffect, useRef, useState } from "react";
import { WsClient, type WsEvent, type WsStatus } from "../api/ws";

/**
 * Single shared WebSocket subscription. Exposes:
 *
 * - {@link WsStatus} status (drives the StatusRow connection dot)
 * - rolling buffer of received events (consumed by the
 *   DecisionFeed). High-volume {@code price.tick} (1 Hz today, up
 *   to 4 Hz per SPEC §14) is filtered out of the feed buffer so a
 *   chatty feed doesn't trigger React re-render storms.
 * - latest {@code price.tick} payload separately, so the TopBar
 *   price tile updates in real time without forcing the feed list
 *   to re-render every tick.
 *
 * Session 41 wires this to {@code /ws/trading} and the
 * {@code TradingWebSocketHandler} now emits all SPEC §14 topics.
 */

const DEFAULT_BUFFER = 200;
/** Event types deliberately omitted from the feed buffer (too chatty). */
const FILTERED_TYPES = new Set<string>(["price.tick"]);

/**
 * A feed event carries a monotonic {@code seq} assigned at RECEPTION. The
 * buffer is SLIDING ({@code slice(1)} once full), so an array index is NOT a
 * stable identity — every eviction would shift all keys and remount every
 * row. {@code seq} survives evictions and reconnects (C5).
 */
export type FeedEvent = WsEvent & { seq: number };

export interface PriceTick {
  bid: number;
  ask: number;
  mid: number;
  spread: number;
  quoteAgeMs?: number;
  delayed?: boolean;
  /** S52a: true if the tick arrived during an active cycle (SPEC §14). */
  cycleActive?: boolean;
  /** Server timestamp from the WS envelope, ISO-8601. */
  ts: string | null;
}

/**
 * S52a: latest market-status snapshot pushed by the backend's
 * StreamLifecycleManager on every TRADEABLE↔CLOSED transition (SPEC §12).
 * The TopBar uses this to distinguish "Market closed" from
 * "Stream disconnected".
 */
export interface MarketStatusSnapshot {
  status: string;
  open: boolean;
  ts: string | null;
}

/**
 * Latest {@code stream.health} snapshot from the backend's
 * StreamLifecycleManager. Lets the TopBar distinguish a feed that is
 * subscribed-but-starved ({@code STREAM_PREEMPTED} — the bot account is
 * logged into the IG platform, or a zombie subscription) from a generic
 * "awaiting tick" cold start.
 */
export interface StreamHealthSnapshot {
  /** Backend StreamHealthState enum name, e.g. "HEALTHY", "STREAM_PREEMPTED". */
  state: string;
  healthy: boolean;
  ts: string | null;
}

export interface UseWsConnectionOptions {
  bufferSize?: number;
}

export interface UseWsConnectionResult {
  status: WsStatus;
  events: FeedEvent[];
  /** Latest price.tick payload, or null while none received this connection. */
  latestPrice: PriceTick | null;
  /**
   * S52a: latest market_status.changed snapshot, or null until the backend
   * publishes one. Persists across cycle boundaries; resets on a new
   * WS connection so a stale snapshot can't masquerade as live state.
   */
  marketStatus: MarketStatusSnapshot | null;
  /**
   * Latest stream.health snapshot, or null until the backend publishes one.
   * Resets on a new WS connection (and is replayed by the backend on connect).
   */
  streamHealth: StreamHealthSnapshot | null;
}

export function useWsConnection(
  path: string,
  opts: UseWsConnectionOptions = {},
): UseWsConnectionResult {
  const bufferSize = opts.bufferSize ?? DEFAULT_BUFFER;
  const [status, setStatus] = useState<WsStatus>("idle");
  const [events, setEvents] = useState<FeedEvent[]>([]);
  // Monotonic per-hook-instance: never reset, so keys stay unique across
  // buffer evictions AND reconnects.
  const seqRef = useRef(0);
  const [latestPrice, setLatestPrice] = useState<PriceTick | null>(null);
  const [marketStatus, setMarketStatus] = useState<MarketStatusSnapshot | null>(null);
  const [streamHealth, setStreamHealth] = useState<StreamHealthSnapshot | null>(null);
  const clientRef = useRef<WsClient | null>(null);

  useEffect(() => {
    // Reset price state on (re)subscription so a stale value from a
    // previous connection cannot linger and look "live" after a path
    // change — this is the partial-un-stubbing defence: on a backend
    // disconnect / new connection we explicitly drop the cached tick.
    setLatestPrice(null);
    setMarketStatus(null);
    setStreamHealth(null);

    const client = new WsClient({
      path,
      onStatus: (s) => {
        setStatus(s);
        // Connection lost or auth dead → drop the cached tick so the
        // TopBar shows the explicit no-feed state instead of a stale
        // number masquerading as live data. Also drop the market-status
        // snapshot: with WS down we don't actually know the backend's
        // current view, so the UI must fall back to the disconnected state
        // rather than continue to claim "market open".
        if (s === "closed" || s === "reconnecting" || s === "auth-failed") {
          setLatestPrice(null);
          setMarketStatus(null);
          setStreamHealth(null);
        }
      },
      onEvent: (e) => {
        if (e.type === "price.tick") {
          const tick = parsePriceTick(e);
          if (tick) setLatestPrice(tick);
          return; // filtered from feed buffer
        }
        if (e.type === "market_status.changed") {
          const snap = parseMarketStatus(e);
          if (snap) setMarketStatus(snap);
          return;
        }
        if (e.type === "stream.health") {
          const snap = parseStreamHealth(e);
          if (snap) setStreamHealth(snap);
          return;
        }
        if (FILTERED_TYPES.has(e.type)) return;
        const stamped: FeedEvent = { ...e, seq: seqRef.current++ };
        setEvents((prev) => {
          const next = prev.length >= bufferSize ? prev.slice(1) : prev.slice();
          next.push(stamped);
          return next;
        });
      },
    });
    clientRef.current = client;
    client.start();
    return () => {
      client.stop();
      clientRef.current = null;
    };
  }, [path, bufferSize]);

  return { status, events, latestPrice, marketStatus, streamHealth };
}

function parseStreamHealth(e: WsEvent): StreamHealthSnapshot | null {
  const d = e.data;
  if (!d || typeof d !== "object") return null;
  const obj = d as Record<string, unknown>;
  const state = typeof obj.state === "string" ? obj.state : null;
  if (state == null) return null;
  return {
    state,
    healthy: typeof obj.healthy === "boolean" ? obj.healthy : false,
    ts: e.ts ?? null,
  };
}

function parseMarketStatus(e: WsEvent): MarketStatusSnapshot | null {
  const d = e.data;
  if (!d || typeof d !== "object") return null;
  const obj = d as Record<string, unknown>;
  const status = typeof obj.status === "string" ? obj.status : null;
  const open = typeof obj.open === "boolean" ? obj.open : null;
  if (status == null || open == null) return null;
  return { status, open, ts: e.ts ?? null };
}

function parsePriceTick(e: WsEvent): PriceTick | null {
  const d = e.data;
  if (!d || typeof d !== "object") return null;
  const obj = d as Record<string, unknown>;
  const mid = toNum(obj.mid);
  const bid = toNum(obj.bid);
  const ask = toNum(obj.ask);
  const spread = toNum(obj.spread);
  // Without mid we cannot drive the TopBar — reject the message so the
  // UI does not advance with partial data.
  if (mid == null || bid == null || ask == null || spread == null) {
    return null;
  }
  return {
    bid,
    ask,
    mid,
    spread,
    quoteAgeMs: toNum(obj.quoteAgeMs) ?? undefined,
    delayed: typeof obj.delayed === "boolean" ? obj.delayed : undefined,
    cycleActive:
      typeof obj.cycle_active === "boolean" ? obj.cycle_active : undefined,
    ts: e.ts ?? null,
  };
}

function toNum(x: unknown): number | null {
  if (typeof x === "number" && Number.isFinite(x)) return x;
  if (typeof x === "string") {
    const n = Number(x);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
